import type {FlexProps as RadixFlexProps} from '@radix-ui/themes';
import {Flex as RadixFlex} from '@radix-ui/themes';
import {memo} from 'react';
import type {PropsWithChildren} from 'react';

import {BASE_UNIT, Size, SPACING_UNIT} from '#theme';
import {whenDefined} from '#utils/general.js';

export type FlexProps = Partial<
  Pick<
    RadixFlexProps,
    | 'align'
    | 'as'
    | 'asChild'
    | 'className'
    | 'direction'
    | 'display'
    | 'flexBasis'
    | 'flexGrow'
    | 'flexShrink'
    | 'justify'
    | 'role'
    | 'style'
    | 'wrap'
  >
> &
  PropsWithChildren<{
    gap?: Size;
    gapX?: Size;
    gapY?: Size;
  }>;

const sizeToGapString = (size: Size) =>
  `${size * (SPACING_UNIT / BASE_UNIT)}px`;

export const Flex = memo(function Flex({
  children,
  gap = Size.Two,
  gapX,
  gapY,
  ...props
}: FlexProps) {
  const gaps = Object.fromEntries(
    Object.entries({gap, gapX, gapY}).map(([key, value]) => [
      key,
      whenDefined(value, sizeToGapString),
    ])
  );

  return (
    <RadixFlex {...props} {...gaps}>
      {children}
    </RadixFlex>
  );
});
